import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, XCircle } from 'lucide-react';

const Home: React.FC = () => {
  return (
    <div className="space-y-24 pb-24">
      {/* Hero Section */}
      <section className="px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-8 animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-serif text-stone-900 leading-[1.1]">
              Turn your skills into a <span className="italic text-brand-dark">profitable business.</span>
            </h1>
            <p className="text-xl text-stone-700 leading-relaxed max-w-lg">
              Helping female solopreneurs attract clients through a clear niche and a strong digital presence in 90 days.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/program" className="bg-stone-900 text-white px-8 py-4 rounded-md text-center font-medium hover:bg-stone-800 transition-all flex items-center justify-center gap-2">
                Explore the 90-Day Program <ArrowRight size={18} />
              </Link>
            </div>
          </div>
          <div className="relative h-[600px] w-full rounded-lg overflow-hidden hidden md:block border-8 border-orange-100">
             <img 
               src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=800&q=80" 
               alt="Productive Office" 
               className="object-cover w-full h-full" 
             />
             <div className="absolute bottom-8 left-8 bg-white/95 backdrop-blur p-6 rounded shadow-lg max-w-xs border border-orange-100">
                <p className="font-serif italic text-lg mb-2 text-stone-800">"I finally launched my business after months of hesitation."</p>
             </div>
          </div>
        </div>
      </section>

      {/* The Problem / Solution / Who For */}
      <section className="max-w-7xl mx-auto px-6 space-y-24">
        
        {/* The Problem */}
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="bg-orange-100 p-12 rounded-2xl relative order-2 md:order-1">
             <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
             <h3 className="text-xl font-bold uppercase tracking-widest text-brand-dark mb-6 relative z-10">The Problem</h3>
             <p className="text-2xl font-serif text-stone-900 mb-8 relative z-10">
               You know you have skills and want more than a corporate job, but you feel stuck.
             </p>
             <ul className="space-y-4 relative z-10">
               {[
                 "You don’t know what to offer",
                 "You’re unsure who to serve",
                 "You’re overwhelmed by branding, websites, and advice",
                 "You’re afraid to quit your job without a plan"
               ].map((item, i) => (
                 <li key={i} className="flex items-start gap-3 text-stone-800">
                   <XCircle size={20} className="text-red-400 mt-1 shrink-0" />
                   <span>{item}</span>
                 </li>
               ))}
             </ul>
             <div className="mt-8 pt-8 border-t border-orange-200 relative z-10">
               <p className="font-medium text-stone-900">
                 You don’t need more information. <br/>
                 <span className="text-brand-dark">You need clarity, structure, and accountability.</span>
               </p>
             </div>
          </div>
          <div className="order-1 md:order-2 space-y-6">
             <h2 className="text-5xl font-serif text-stone-900">Is this you?</h2>
             <p className="text-lg text-stone-600">
               Most talented women stay in jobs they've outgrown because the path to entrepreneurship feels too chaotic.
             </p>
          </div>
        </div>

        {/* The Solution */}
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
             <h2 className="text-5xl font-serif text-stone-900">The Solution</h2>
             <p className="text-lg text-stone-600">
               That’s where Productive Folks comes in. We help ambitious corporate women build a business before quitting.
             </p>
             <div className="bg-white p-8 rounded-xl border border-orange-100 shadow-sm space-y-4">
                <h4 className="font-bold text-lg mb-2">We provide:</h4>
                <ul className="space-y-3">
                  {[
                    "A clear niche and help identifying your ideal client",
                    "A simple, income-generating offer",
                    "A professional digital presence",
                    "Support and accountability every step of the way"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-stone-800">
                      <CheckCircle size={20} className="text-brand-dark mt-1 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
             </div>
          </div>
          <div className="relative h-[500px] w-full bg-orange-200 rounded-2xl overflow-hidden">
             <img src="https://images.pexels.com/photos/1181547/pexels-photo-1181547.jpeg" alt="Team working on business solutions" className="object-cover w-full h-full opacity-95" />
          </div>
        </div>

        {/* Who This Is For */}
        <div className="bg-stone-900 text-orange-50 p-12 md:p-24 rounded-3xl text-center space-y-12">
           <h2 className="text-4xl font-serif text-white">Who This Is For</h2>
           <div className="grid md:grid-cols-2 gap-12 text-left max-w-4xl mx-auto">
              <div className="space-y-6">
                 <div className="flex items-center gap-3 text-green-400 font-bold text-lg">
                   <CheckCircle /> Perfect For:
                 </div>
                 <ul className="space-y-4">
                   <li className="flex gap-3">
                     <span className="text-green-400">✔</span>
                     Talented women with skills but no clear business direction
                   </li>
                   <li className="flex gap-3">
                     <span className="text-green-400">✔</span>
                     Corporate women planning their exit
                   </li>
                   <li className="flex gap-3">
                     <span className="text-green-400">✔</span>
                     Women who want a structured, realistic path to entrepreneurship
                   </li>
                 </ul>
              </div>
              <div className="space-y-6">
                 <div className="flex items-center gap-3 text-red-400 font-bold text-lg">
                   <XCircle /> Not For:
                 </div>
                 <ul className="space-y-4">
                   <li className="flex gap-3">
                     <span className="text-red-400">🚫</span>
                     People with excuses
                   </li>
                   <li className="flex gap-3">
                     <span className="text-red-400">🚫</span>
                     Overnight success seekers
                   </li>
                 </ul>
              </div>
           </div>
        </div>
      </section>

      {/* About Me Section */}
      <section className="bg-white py-24 border-t border-orange-100">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
             <div className="relative order-2 md:order-1">
                <img 
                  src="https://images.unsplash.com/photo-1769794371055-54436b54577e?q=80&w=800&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                  alt="Zahiya Imam" 
                  className="rounded-lg shadow-xl object-cover object-top w-full aspect-[3/4] transition-all duration-500 hover:scale-[1.01]" 
                />
                <div className="absolute -bottom-6 -right-6 bg-orange-100 p-8 rounded-tl-3xl shadow-lg hidden lg:block border border-orange-200">
                   <p className="font-serif text-2xl text-stone-900">"Focus on progress,<br/>not perfection."</p>
                </div>
             </div>
             <div className="space-y-6 order-1 md:order-2">
                <span className="text-orange-600 font-bold tracking-widest text-sm uppercase">About Me</span>
                <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">Zahiya Imam</h2>
                <div className="space-y-4 text-lg text-stone-600 leading-relaxed">
                    <p>Before I started Productive Folks, I was stuck in a cycle many ambitious women know too well, juggling a demanding job while trying to “figure out” a business on the side.</p>
                    <p>I had ideas everywhere. Notes, documents, half-finished plans but no clear direction. I kept refining and tweaking, hoping clients would magically find me before I put myself out there.</p>
                    <p className="font-bold text-stone-900">It didn’t happen.</p>
                    <p className="italic text-stone-800 border-l-4 border-orange-400 pl-4 py-2 bg-orange-50 my-6">I was 'busy' but not building.</p>
                    <p>Once I stopped chasing perfection and focused on progress, everything changed. I defined my niche, created a simple offer, and built a professional digital presence that actually reflected my value.</p>
                    
                    <p className="font-bold text-stone-900 pt-4">That’s why today, I help corporate women and aspiring female solopreneurs who:</p>
                    <ul className="space-y-3 ml-2">
                        {[
                            "Feel stuck with too many ideas and no clear business direction",
                            "Want to start a business before quitting their job",
                            "Know they have valuable skills, but don’t know how to package or position them",
                            "Feel overwhelmed by branding, websites, and “online business” noise"
                        ].map((item, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <span className="text-orange-500 mt-1.5 text-xs">●</span>
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>

                    <p className="pt-4">I’ve been where you are, tired after a long workday, still trying to build something meaningful at night, unsure if you’re doing the right things.</p>
                    <p>The good news? You don’t need to hustle harder or wait for the perfect moment. <span className="font-bold text-brand-dark">You need a plan, structure, and the right support.</span></p>
                    <p>Let’s build your business, position your services professionally and attract your ideal clients.</p>
                </div>
                
                <div className="pt-8">
                    <Link to="/work-with-me" className="bg-stone-900 text-white px-8 py-4 rounded font-medium hover:bg-stone-800 transition-colors inline-flex items-center gap-2">
                        Work With Me <ArrowRight size={18}/>
                    </Link>
                </div>
             </div>
        </div>
      </section>
    </div>
  );
};

export default Home;